// API Service with Firebase Auth
import { auth } from '../firebase';
import { API_BASE_URL } from './apiBase';
import { SignificanceResult, IdentityHistory, AttributionResult, AgentEvolution, Scoreboard } from '../types';


class ApiService {
  async getAuthToken(): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      const token = await user.getIdToken();
      return token;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  async authenticatedFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });
  }

  // Generic GET method
  async get(endpoint: string): Promise<any> {
    const response = await this.authenticatedFetch(endpoint, {
      method: 'GET',
    });
    
    if (!response.ok) {
      throw new Error(`GET request failed: ${response.statusText}`);
    }
    
    return response.json();
  }

  // Generic POST method
  async post(endpoint: string, data: any = {}): Promise<any> {
    const response = await this.authenticatedFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`POST request failed: ${response.statusText}`);
    }
    
    return response.json();
  }

  // ============================================================
  // AI AGENTS API
  // ============================================================

  // Get agent leaderboard (sorted by P&L) - requires auth
  async getAgentLeaderboard(limit: number = 10, period?: string): Promise<any[]> {
    try {
      console.log('Fetching leaderboard with auth...');
      let url = `/agents/leaderboard?limit=${limit}`;
      if (period && period !== 'ALL') {
        url += `&period=${period}`;
      }
      const response = await this.authenticatedFetch(url);
      if (!response.ok) {
        const text = await response.text();
        console.error('Leaderboard error:', response.status, text);
        throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('Leaderboard data received:', data);
      return data;
    } catch (error) {
      console.error('getAgentLeaderboard error:', error);
      throw error;
    }
  }

  // Get single agent details - requires auth
  async getAgentDetail(agentId: string): Promise<any> {
    const response = await this.authenticatedFetch(`/agents/${agentId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch agent: ${response.statusText}`);
    }
    return response.json();
  }

  // Get NAV history for charting
  async getAgentNavHistory(agentId: string, period: string = '30d'): Promise<any> {
    const response = await this.authenticatedFetch(`/agents/${agentId}/nav-history?period=${period}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch NAV history: ${response.statusText}`);
    }
    return response.json();
  }

  // Get period performance metrics
  async getAgentPeriodPerformance(agentId: string, period: string): Promise<any> {
    const response = await this.authenticatedFetch(`/agents/${agentId}/performance/${period}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch period performance: ${response.statusText}`);
    }
    return response.json();
  }

  // Get statistical-significance testing for an agent (Spec 2.2)
  async getAgentSignificance(agentId: string): Promise<SignificanceResult> {
    const response = await this.authenticatedFetch(`/agents/${agentId}/significance`);
    if (!response.ok) {
      throw new Error(`Failed to fetch significance: ${response.statusText}`);
    }
    return response.json();
  }

  // Get an agent's identity version history (Spec 2.1)
  async getAgentIdentityHistory(agentId: string, limit: number = 10): Promise<IdentityHistory> {
    const response = await this.authenticatedFetch(`/agents/${agentId}/identity/history?limit=${limit}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch identity history: ${response.statusText}`);
    }
    return response.json();
  }

  // Get an agent's evolution time-series (Spec 2.4)
  async getAgentEvolution(agentId: string, period: string = '90d', metrics?: string[]): Promise<AgentEvolution> {
    let url = `/agents/${agentId}/evolution?period=${period}`;
    if (metrics && metrics.length) url += `&metrics=${metrics.join(',')}`;
    const response = await this.authenticatedFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch evolution: ${response.statusText}`);
    }
    return response.json();
  }

  // Get performance attribution for an agent (Spec 2.5)
  async getAttribution(agentId: string, dimensions?: string[], period: string = 'ALL'): Promise<AttributionResult> {
    let url = `/analytics/attribution?agent_id=${agentId}&period=${period}`;
    if (dimensions && dimensions.length) url += `&dimensions=${dimensions.join(',')}`;
    const response = await this.authenticatedFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch attribution: ${response.statusText}`);
    }
    return response.json();
  }

  // B-015: the scoreboard (each agent vs SPY, QQQ, JEPI, QYLD, YMAX), served from Firestore only.
  // Throws on any failure so the component shows an explicit error, never an empty table.
  async getScoreboard(): Promise<Scoreboard> {
    const response = await this.authenticatedFetch('/scoreboard');
    if (!response.ok) {
      let detail = response.statusText;
      try {
        const body = await response.json();
        if (body && body.error) detail = body.error;
      } catch {
        // the status text is the detail
      }
      throw new Error(`Scoreboard unavailable: ${detail || response.status}`);
    }
    return response.json();
  }

  // Get benchmark series for a period: SPY and QQQ, and the income funds JEPI, QYLD and YMAX (B-050)
  async getBenchmarks(period: string, symbols: string[] = ['SPY', 'QQQ'], fromDate?: string): Promise<Record<string, Array<{ date: string; price: number; return_pct: number; provisional?: boolean }>>> {
    let url = `/benchmarks?period=${period}&symbols=${symbols.join(',')}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    const response = await this.authenticatedFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch benchmarks: ${response.statusText}`);
    }
    return response.json();
  }

  // Get all agent trades with agent metadata - requires auth
  async getAgentTrades(status?: 'open' | 'closed'): Promise<any[]> {
    const params = status ? `?status=${status}` : '';
    const response = await this.authenticatedFetch(`/agents/trades${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch agent trades: ${response.statusText}`);
    }
    return response.json();
  }

  // Trigger orchestration cycle (fires Cloud Scheduler job and returns immediately)
  async runOrchestration(): Promise<any> {
    const response = await this.authenticatedFetch('/agents/orchestrate', {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to trigger orchestration: ${response.statusText}`);
    }
    return response.json();
  }

  // Refresh live prices for all open agent positions
  async refreshPositions(): Promise<any> {
    const response = await this.authenticatedFetch('/agents/refresh-prices', {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to refresh prices: ${response.statusText}`);
    }
    return response.json();
  }

  // Reseed the starter agents (admin only). B-057: POST only, so a page load or a link can never run
  // it; a reseed rewrites every agent's entry and exit rules with the starter defaults.
  async initAgents(): Promise<any> {
    const response = await this.authenticatedFetch('/agents/init', { method: 'POST' });
    if (!response.ok) {
      throw new Error(`Failed to init agents: ${response.statusText}`);
    }
    return response.json();
  }

  // Get forum feed
  async getForumFeed(limit: number = 50, symbol?: string): Promise<any> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (symbol) params.set('symbol', symbol);
    const response = await this.authenticatedFetch(`/agents/forum?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch forum: ${response.statusText}`);
    }
    return response.json();
  }
  // ============================================================
  // DAILY SUMMARY (Today tab)
  // ============================================================

  async getDailySummary(): Promise<any> {
    const response = await this.authenticatedFetch('/summary/today');
    if (!response.ok) {
      throw new Error(`Failed to fetch daily summary: ${response.statusText}`);
    }
    return response.json();
  }

  // ============================================================
  // ADMIN API
  // ============================================================

  async getAdminUsers(): Promise<any[]> {
    const response = await this.authenticatedFetch('/admin/users');
    if (!response.ok) {
      throw new Error(`Failed to fetch users: ${response.statusText}`);
    }
    return response.json();
  }

  async recalculatePerformance(): Promise<any> {
    const response = await this.authenticatedFetch('/admin/recalculate-performance', {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to recalculate: ${response.statusText}`);
    }
    return response.json();
  }

  // Newsletter subscription toggle
  async toggleNewsletter(enabled: boolean): Promise<any> {
    const response = await this.authenticatedFetch('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
    if (!response.ok) {
      throw new Error(`Failed to update newsletter: ${response.statusText}`);
    }
    return response.json();
  }

  // Admin: toggle newsletter for a specific user
  async adminToggleNewsletter(uid: string, enabled: boolean): Promise<any> {
    const response = await this.authenticatedFetch('/admin/toggle-newsletter', {
      method: 'POST',
      body: JSON.stringify({ uid, enabled }),
    });
    if (!response.ok) {
      throw new Error(`Failed to toggle newsletter: ${response.statusText}`);
    }
    return response.json();
  }
}

export const apiService = new ApiService();